import {
  HalfMoon,
  HelpCircle,
  HomeSimple,
  Key,
  Menu,
  MultiplePages,
  ProfileCircle,
  ViewGrid,
} from "iconoir-react";
import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { IconButton } from "@/components/ui/icon-button";
import { Icon } from "@/design-system/icons/icon";
import { useTheme } from "@/design-system/theme/theme-provider";
import { m } from "@/i18n/en";
import { cn } from "@/lib/cn";
import { AccountMenu } from "./account-menu";
import { Brand } from "./brand";
import { SearchPalette } from "./search-palette";

const navigation = [
  { to: "/", label: m.navigation.overview, icon: HomeSimple, end: true },
  { to: "/personal-info", label: m.navigation.personal, icon: ProfileCircle },
  { to: "/security", label: m.navigation.security, icon: Key },
  { to: "/sessions", label: m.navigation.sessions, icon: MultiplePages },
  { to: "/products", label: m.navigation.products, icon: ViewGrid },
] as const;

function Navigation({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav aria-label={m.navigation.label} className="grid gap-1">
      {navigation.map((item) => (
        <NavLink
          className={({ isActive }) =>
            cn(
              "flex h-11 items-center gap-3 rounded-[var(--radius-md)] px-3 text-sm font-medium text-secondary transition-colors hover:bg-hover hover:text-foreground",
              isActive && "bg-primary-subtle text-primary",
            )
          }
          {...("end" in item ? { end: item.end } : {})}
          key={item.to}
          onClick={onNavigate}
          to={item.to}
        >
          <span className="grid size-7 shrink-0 place-items-center">
            <Icon glyph={item.icon} />
          </span>
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  return (
    <div className="min-h-screen bg-canvas">
      <header className="sticky top-0 z-30 flex h-[var(--layout-header)] items-center justify-between border-b border-line bg-canvas/95 px-4 backdrop-blur-sm sm:px-7">
        <div className="flex items-center gap-1">
          <IconButton
            className="lg:hidden"
            label={m.navigation.open}
            onClick={() => setMobileOpen(true)}
          >
            <Icon glyph={Menu} />
          </IconButton>
          <Brand />
        </div>
        <div className="flex items-center gap-1">
          <SearchPalette className="mr-2 xl:fixed xl:top-[calc(var(--layout-header)+2.5rem)] xl:right-[max(2.5rem,calc((100vw-var(--layout-content)-var(--layout-sidebar))/2+2.5rem))] xl:z-20" />
          <HelpDrawer />
          <ThemeShortcut />
          <AccountMenu />
        </div>
      </header>
      <div className="mx-auto flex max-w-[calc(var(--layout-content)+var(--layout-sidebar))]">
        <aside className="sticky top-[var(--layout-header)] hidden h-[calc(100vh-var(--layout-header))] w-[var(--layout-sidebar)] shrink-0 flex-col justify-between px-5 py-8 lg:flex">
          <Navigation />
          <p className="px-3 text-xs leading-5 text-muted">{m.privateNote}</p>
        </aside>
        <main
          className="relative min-w-0 flex-1 px-5 py-8 sm:px-8 lg:px-10 lg:py-10"
          id="main-content"
          key={location.pathname}
        >
          {children}
        </main>
      </div>
      <Drawer onOpenChange={setMobileOpen} open={mobileOpen}>
        <DrawerContent>
          <DrawerHeader closeLabel={m.close}>
            <DrawerTitle>
              <Brand />
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            <Navigation onNavigate={() => setMobileOpen(false)} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function ThemeShortcut() {
  const { appearance, setAppearance } = useTheme();
  return (
    <IconButton
      label={m.theme.quickTheme}
      onClick={() => setAppearance(appearance === "dark" ? "light" : "dark")}
    >
      <Icon glyph={HalfMoon} />
    </IconButton>
  );
}

function HelpDrawer() {
  return (
    <Drawer>
      <DrawerTrigger asChild>
        <IconButton label={m.help.open}>
          <Icon glyph={HelpCircle} />
        </IconButton>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader closeLabel={m.close}>
          <DrawerTitle className="text-xl font-semibold">
            {m.help.title}
          </DrawerTitle>
          <p className="mt-1 text-sm text-secondary">{m.help.description}</p>
        </DrawerHeader>
        <DrawerBody>
          <ul className="divide-y divide-line border-y border-line">
            <li className="py-5">
              <p className="font-semibold">{m.help.signIn}</p>
              <p className="mt-1 text-sm text-secondary">
                {m.help.signInDescription}
              </p>
            </li>
            <li className="py-5">
              <p className="font-semibold">{m.help.profile}</p>
              <p className="mt-1 text-sm text-secondary">
                {m.help.profileDescription}
              </p>
            </li>
            <li className="py-5">
              <p className="font-semibold">{m.help.sessions}</p>
              <p className="mt-1 text-sm text-secondary">
                {m.help.sessionsDescription}
              </p>
            </li>
          </ul>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
